import {FiAlertCircle,FiCheck,FiChevronDown,FiLoader,FiSquare,FiX} from 'react-icons/fi';
import './BatchImportProgress.css';

const activeStatuses=new Set(['checking','preparing','uploading','transcribing']);
const finishedStatuses=new Set(['complete','failed','rejected','stopped']);
const errorStatuses=new Set(['failed','rejected']);

export function batchStepLabel(item) {
  switch(item?.status){
    case 'checking':return 'Checking video';
    case 'ready':return 'Ready to upload';
    case 'preparing':return 'Preparing video';
    case 'uploading':{
      const match=String(item.detail||'').match(/(\d{1,3})%/);
      return match?'Uploading · '+Math.min(100,Number(match[1]))+'%':'Uploading video';
    }
    case 'transcribing':return 'Transcribing audio';
    case 'complete':return 'Transcribed';
    case 'rejected':return 'Could not import';
    case 'failed':return 'Needs attention';
    case 'stopped':return 'Not uploaded';
    default:return 'Waiting';
  }
}

export default function BatchImportProgress({report,stopping=false,onStop,onDismiss}) {
  const items=report?.items||[];
  if(!items.length)return null;
  const total=items.length;
  const completed=items.filter(item=>item.status==='complete').length;
  const errors=items.filter(item=>errorStatuses.has(item.status)).length;
  const settled=items.filter(item=>finishedStatuses.has(item.status)).length;
  const activeIndex=items.findIndex(item=>activeStatuses.has(item.status));
  const currentIndex=activeIndex>=0?activeIndex:items.findIndex(item=>item.status==='ready'||item.status==='queued');
  const current=currentIndex>=0?items[currentIndex]:null;
  const active=Boolean(report.active);
  const title=active?'Importing interviews':errors?'Import finished with issues':stopping||items.some(i=>i.status==='stopped')?'Import stopped':'Interviews ready';
  return (
    <section className={`batch-upload-report${active?' is-active':''}${errors?' has-errors':''}`} aria-label="Batch interview import">
      <div className="batch-upload-heading">
        <div className="batch-upload-heading-title">
          {active?<FiLoader className="batch-import-spin" aria-hidden="true"/>:errors?<FiAlertCircle aria-hidden="true"/>:<FiCheck aria-hidden="true"/>}
          <strong>{title}</strong>
        </div>
        <span className="batch-upload-count" aria-label={`${completed} of ${total} interviews transcribed`}>{completed}/{total}</span>
      </div>
      <div className="batch-upload-track" role="progressbar" aria-valuemin={0} aria-valuemax={total}
        aria-valuenow={settled} aria-valuetext={`${settled} of ${total} interviews processed`}>
        <span style={{width:`${(settled/total)*100}%`}}/>
        {active&&<i className="batch-upload-track-working" aria-hidden="true"/>}
      </div>
      {active&&current&&<div className="batch-upload-current" role="status" aria-live="polite">
        <strong title={current.name}>{current.name}</strong>
        <span>{stopping?'Finishing current interview…':batchStepLabel(current)}</span>
      </div>}
      {!active&&<p className="batch-upload-outcome">
        {errors?`${completed} ready · ${errors} need attention`:completed===total?'All interviews transcribed successfully.':`${completed} transcribed · ${total-settled} remaining`}
      </p>}
      <div className="batch-upload-bottom">
        <details className="batch-upload-details" key={active?'running':errors?'issues':'finished'} open={!active&&errors>0}>
          <summary><span>{!active&&errors?'Review files':'View queue'}</span><FiChevronDown aria-hidden="true"/></summary>
          <ul className="batch-upload-list">
            {items.map((item,index)=><li key={index} className={`batch-upload-item is-${item.status}`}>
              <span className="batch-upload-row-icon">
                {item.status==='complete'?<FiCheck aria-hidden="true"/>:
                errorStatuses.has(item.status)?<FiAlertCircle aria-hidden="true"/>:
                activeStatuses.has(item.status)?<FiLoader className="batch-import-spin" aria-hidden="true"/>:<span aria-hidden="true">{index+1}</span>}
              </span>
              <div>
                <strong title={item.name}>{item.name}</strong>
                <small>{errorStatuses.has(item.status)?item.detail:item.status==='complete'?item.detail:batchStepLabel(item)}</small>
              </div>
            </li>)}
          </ul>
        </details>
        {active?<button type="button" className="batch-upload-stop" disabled={stopping}
          onClick={onStop} title="Finish the current interview, then stop the remaining queue">
          <FiSquare aria-hidden="true"/>{stopping?'Stopping…':'Stop'}
        </button>:<button type="button" className="batch-upload-dismiss" aria-label="Dismiss import summary"
          onClick={onDismiss} title="Dismiss import summary"><FiX aria-hidden="true"/></button>}
      </div>
      {active&&<p className="batch-upload-hint">Keep this tab open until the batch finishes.</p>}
    </section>
  );
}
